import { useEffect } from 'react'

/** `?focus=` (Frontend §6.1): a row id from a C-05 arrow; any other value is ignored. */
export const parseFocus = (raw: Record<string, unknown>): { focus?: string } =>
  typeof raw.focus === 'string' && /^[\w-]{1,64}$/.test(raw.focus) ? { focus: raw.focus } : {}

/** The focused row's class: it flashes once when drawn (`row-focus`, staff.css). */
export const focusClass = (id: string, focus: string | undefined): string => (id === focus ? 'row-focus' : '')

/** How long to wait for the row to be in the document (a first page load can mount before it attaches). */
const TRIES = 40
const EVERY_MS = 25

/**
 * "Take me there" from the Booking map (C-05 → C-06 / C-07 / C-08, ported from the mockup's `useFocusRow`): the row
 * `[data-row={focus}]` is scrolled into view; the row itself carries `focusClass` so it flashes.
 */
export function useFocusRow(focus: string | undefined): void {
  useEffect(() => {
    if (!focus) return
    let tries = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const attempt = () => {
      const el = document.querySelector<HTMLElement>(`[data-row="${CSS.escape(focus)}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      else if (tries < TRIES) {
        tries += 1
        timer = setTimeout(attempt, EVERY_MS)
      }
    }
    attempt()
    return () => {
      clearTimeout(timer)
    }
  }, [focus])
}
